// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CanvasDrawer } from './CanvasDrawer';
import { MapContainer } from '@shared/commons/Heatmap/Maps/MapContainer';
import { HeatmapSectorRenderer } from '../../panels/Heatmap/Renderers/HeatmapSectorRenderer';
import { HeatmapIndustryRenderer } from '../../panels/Heatmap/Renderers/HeatmapIndustryRenderer';
import { HeatmapSymbolRenderer } from '../../panels/Heatmap/Renderers/HeatmapSymbolRenderer';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { HeatmapSettings } from '@shared/commons/Heatmap/Settings/HeatmapSettings';
import { PartialIntervalManager } from '@shared/commons/Heatmap/PartialIntervals/PartialIntervalManager';
import { HeatmapLogoProvider } from '../../panels/Heatmap/HeatmapLogoProvider';
import { EventEmitter } from 'events';
import { ThemeManager } from '../../misc/ThemeManager';
import { SolidBrush } from '@shared/commons/Graphics';
import { Resources } from '@shared/localizations/Resources';
import { Cursors } from '@shared/commons/Cursors';
export class HeatMapDrawer extends CanvasDrawer {
    constructor() {
        super();
        this._eventEmitter = new EventEmitter();
        this.width = 0;
        this.height = 0;
        this._sectorRenderers = [];
        this._industryRenderers = [];
        this._symbolRenderers = [];
        this._mousePoint = Point.Empty();
        this._currentCursor = Cursors.Default;
        this.onMouseDown = (event) => { };
        this.onMouseMove = (event) => {
            this._mousePoint = new Point(event.offsetX, event.offsetY);
            if (isNullOrUndefined(this._hoveredSymbolRenderer) ||
                !this._hoveredSymbolRenderer.isHovered(this._mousePoint)) {
                for (let i = this._symbolRenderers.length - 1; i >= 0; i--) {
                    if (this._symbolRenderers[i].isHovered(this._mousePoint)) {
                        this._hoveredSymbolRenderer = this._symbolRenderers[i];
                        const data = !isNullOrUndefined(this._hoveredSymbolRenderer)
                            ? this._hoveredSymbolRenderer.getData()
                            : null;
                        this._eventEmitter.emit('onHoveredSymbolChanged', data);
                        break;
                    }
                }
            }
            this._currentCursor = this.determineCursor();
        };
        this.onMouseUp = (event) => { };
        this.onMouseLeave = (event) => {
            this._mousePoint = Point.Empty();
        };
        this.onClick = (event) => {
            const isUpdateMap = this.processSelectSector() || this.processSelectIndustry() || this.processActionSector();
            if (isUpdateMap) {
                this.updateMap();
            }
            else {
                this.processSelectInstrument();
            }
        };
        this.onSizeChanged = (width, height) => {
            this.width = Math.round(width);
            this.height = Math.round(height);
            this.updateMap();
        };
        this._settings = new HeatmapSettings();
        this._partialIntervalManager = new PartialIntervalManager();
        this._mapContainer = new MapContainer(this._partialIntervalManager);
        this.applyColors();
    }
    getStepMultiplier() {
        return this._partialIntervalManager.getStepMultiplier();
    }
    setStepMultiplier(stepMultiplier) {
        this._partialIntervalManager.setStepMultiplier(stepMultiplier);
    }
    populateIntervals() {
        this._partialIntervalManager.populateIntervals();
    }
    getIntervals() {
        return this._partialIntervalManager.getIntervals();
    }
    applySettings() {
        this.applyColors();
        this.updateMap();
    }
    getSettings() {
        return this._settings;
    }
    populate(heatmapData) {
        HeatmapLogoProvider.loadLogos(heatmapData);
        this._mapContainer.Populate(heatmapData);
        this.removeRenderers();
        this.createRenderers();
        this.updateMap();
    }
    getData() {
        return this._mapContainer.getData();
    }
    Draw(ctx) {
        if (this.width === 0 || this.height === 0) {
            return;
        }
        ctx.fillStyle = ThemeManager.colors.chartBackground;
        ctx.fillRect(0, 0, this.width, this.height);
        if (this._mapContainer.isEmpty || !this._mapContainer.isVisible) {
            this.drawNoData(ctx);
            return;
        }
        for (const renderer of this._sectorRenderers) {
            renderer.draw(ctx, this._mousePoint);
        }
        for (const renderer of this._industryRenderers) {
            renderer.draw(ctx, this._mousePoint);
        }
        for (const renderer of this._symbolRenderers) {
            renderer.draw(ctx, this._mousePoint);
        }
        for (const renderer of this._industryRenderers) {
            renderer.drawSelection(ctx, this._mousePoint);
        }
        for (const renderer of this._sectorRenderers) {
            renderer.drawSelection(ctx, this._mousePoint);
        }
    }
    applyColors() {
        const viewSettings = this._settings.viewSettings;
        this._partialIntervalManager.applyColors(viewSettings.minColor, viewSettings.midColor, viewSettings.maxColor);
        this.setIsDirty();
    }
    drawNoData(ctx) {
        let noDataText = '';
        if (this._mapContainer.isEmpty) {
            noDataText = Resources.getResource('general.NoData...');
        }
        else if (!this._mapContainer.isVisible) {
            noDataText = Resources.getResource('general.NoDataMatchingYourCriteria');
        }
        if (!isValidString(noDataText)) {
            return;
        }
        const foregroundBrush = new SolidBrush(ThemeManager.colors.chartNoDataForeground);
        const font = ThemeManager.Fonts.Font_14F_regular;
        const r = new Rectangle(0, 0, this.width, this.height);
        const gap = 16;
        const imgSize = 48;
        const textSize = ctx.GetTextSize(noDataText, font, false);
        const noDataRect = new Rectangle(0, 0, textSize.width, imgSize + gap + textSize.height);
        noDataRect.X = r.Width / 2 - textSize.width / 2;
        noDataRect.Y = r.Height / 2 - noDataRect.Height / 2;
        const imgRect = new Rectangle(noDataRect.X + noDataRect.Width / 2 - imgSize / 2, noDataRect.Y, imgSize, imgSize);
        const textRect = new Rectangle(noDataRect.X, imgRect.Y + imgSize + gap, noDataRect.Width, textSize.height);
        if (textRect.X > 0) {
            const img = ThemeManager.NewImages.heatmapIcon;
            ctx.DrawCenteredImage(img.image, imgRect);
            ctx.DrawStringInRect(noDataText, font, foregroundBrush, textRect, false);
        }
        else {
            ctx.DrawString(noDataText, font, foregroundBrush, r.X, r.Y);
        }
    }
    createRenderers() {
        for (const sector of this._mapContainer.getSectorMapList()) {
            this._sectorRenderers.push(new HeatmapSectorRenderer(sector, this._settings.viewSettings));
            for (const industyMap of sector.items) {
                this._industryRenderers.push(new HeatmapIndustryRenderer(industyMap, this._settings.viewSettings));
                for (const symbolMap of industyMap.items) {
                    this._symbolRenderers.push(new HeatmapSymbolRenderer(symbolMap, this._settings.viewSettings));
                }
            }
        }
    }
    removeRenderers() {
        this._sectorRenderers.length = 0;
        this._industryRenderers.length = 0;
        this._symbolRenderers.length = 0;
    }
    processSelectSector() {
        const hoveredSector = this.getHoveredSector();
        if (isNullOrUndefined(hoveredSector) ||
            hoveredSector.isSelected ||
            !isNullOrUndefined(hoveredSector.selectedIndustry)) {
            return false;
        }
        this._mapContainer.selectSector(hoveredSector);
        return true;
    }
    processActionSector() {
        const hoveredSector = this.getHoveredSector();
        if (isNullOrUndefined(hoveredSector) ||
            (!hoveredSector.isSelected && isNullOrUndefined(hoveredSector.selectedIndustry))) {
            return false;
        }
        for (const sectorRenderer of this._sectorRenderers) {
            if (sectorRenderer.isAllHovered(this._mousePoint)) {
                this._mapContainer.clearSelection();
                return true;
            }
            else if (sectorRenderer.isSectorNameHovered(this._mousePoint)) {
                this._mapContainer.clearSelection();
                this._mapContainer.selectSector(hoveredSector);
                return true;
            }
        }
        return false;
    }
    processSelectIndustry() {
        const hoveredIndustry = this.getHoveredIndustry();
        if (isNullOrUndefined(hoveredIndustry) || hoveredIndustry.isSelected) {
            return false;
        }
        this._mapContainer.selectIndustry(hoveredIndustry);
        return true;
    }
    processSelectInstrument() {
        const hoveredInstrument = this.getHoveredInstrument();
        if (!isNullOrUndefined(hoveredInstrument)) {
            this._eventEmitter.emit('onSymbolClicked', hoveredInstrument);
            return true;
        }
        return true;
    }
    getHoveredSector() {
        const sectors = this._mapContainer.getSectorMapList();
        for (const sector of sectors) {
            if (!sector.Visible) {
                continue;
            }
            if (sector.headerRectangle.Contains(this._mousePoint.X, this._mousePoint.Y)) {
                return sector;
            }
        }
        return undefined;
    }
    getHoveredIndustry() {
        const industries = this._mapContainer.getIndustryMapList();
        for (const industry of industries) {
            if (!industry.Visible) {
                continue;
            }
            if (industry.headerRectangle.Contains(this._mousePoint.X, this._mousePoint.Y)) {
                return industry;
            }
        }
        return undefined;
    }
    getHoveredInstrument() {
        var _a;
        if (!isNullOrUndefined(this._hoveredSymbolRenderer)) {
            const instrument = (_a = this._hoveredSymbolRenderer.getData()) === null || _a === void 0 ? void 0 : _a.Instrument;
            if (!isNullOrUndefined(instrument)) {
                return instrument;
            }
        }
        return undefined;
    }
    ThemeChanged() {
        this._settings.viewSettings.themeChanged();
        this.applySettings();
    }
    getCursor() {
        return this._currentCursor;
    }
    updateMap() {
        if (isNullOrUndefined(this._mapContainer)) {
            return;
        }
        const sectorHeaderHeight = ThemeManager.getMediumHeight();
        const industryHeaderHeight = ThemeManager.getSmallHeight();
        this._mapContainer.updateMap(this.width, this.height, sectorHeaderHeight, industryHeaderHeight);
        if (!isNullOrUndefined(this._hoveredSymbolRenderer)) {
            this._eventEmitter.emit('onHoveredSymbolChanged', this._hoveredSymbolRenderer.getData());
        }
        this.setIsDirty();
    }
    subscribeOnHoveredSymbolChanged(callback) {
        this._eventEmitter.on('onHoveredSymbolChanged', callback);
    }
    subscribeOnSymbolClicked(callback) {
        this._eventEmitter.on('onSymbolClicked', callback);
    }
    unsubscribeOnHoveredSymbolChanged(callback) {
        this._eventEmitter.off('onHoveredSymbolChanged', callback);
    }
    unsubscribeOnSymbolClicked(callback) {
        this._eventEmitter.off('onSymbolClicked', callback);
    }
    determineCursor() {
        if ((!isNullOrUndefined(this._hoveredSymbolRenderer) &&
            this._hoveredSymbolRenderer.isHovered(this._mousePoint)) ||
            !isNullOrUndefined(this.getHoveredIndustry()) ||
            !isNullOrUndefined(this.getHoveredSector())) {
            return Cursors.Hand;
        }
        else {
            return Cursors.Default;
        }
    }
}
